import React, { ChangeEvent, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormContext } from 'react-hook-form';

import { useOnEscapePress } from '@/hooks/useOnEscapePress';
import { AutoComplete, HandleNumber, HandleString } from '@/components/Form/types';
import { StyledError } from '@/components/Form/styles';
import { useOnClickOutside } from '@/hooks/useOnClickOutside';
import { MultiSelectRows } from './components/MultiSelectRows';
import { InputBasic } from '@/components/Form/components/Input/InputBasic';
import { StyledDropdownWrapper } from '@/components/Dropdown/StyledDropdownWrapper';
import { StyledDropdownMultiSelect } from './styles';
import { useDropdown } from '@/hooks/useDropdown';
import { MultiSelectProps, MultiSelectOption } from './types';

export function MultiSelect({
  label,
  name,
  placeholder,
  sort,
  multiple = false,
  limit,
  icon = 'chevron-down',
  disabled,
  required,
  loading,
  hiddenLabel,
  options,
  defaultSelectOnload,
  showValues,
  valueAsNumber,
  fullwidth,
  newTheme,
  handleSideEffect,
  searchString: searchStringControlled,
  setSearchString: setSearchStringControlled,
}: MultiSelectProps): JSX.Element {
  const { t } = useTranslation('common');
  // The trigger input opens the dropdown via onFocus, so useDropdown must not
  // restore focus on close — blur() below returns focus to the input itself.
  const { containerRef, open, close, isOpen } = useDropdown<HTMLDivElement>({ restoreFocusOnClose: false });
  const [hasFocus, setHasFocus] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [searchStringInternal, setSearchStringInternal] = useState('');
  const [localOptions, setLocalOptions] = useState<MultiSelectOption[]>([]);
  // Guards the input's onFocus while blur() programmatically returns focus to
  // it, so closing the dropdown does not immediately re-open it.
  const skipOpenOnFocus = useRef(false);
  const { formState, watch, setValue, register, reset, getValues } = useFormContext();
  const watched = watch(name);
  const isMultiple = limit === 1 ? false : multiple;
  const error = formState.errors[name];

  const baseId = useId();
  const listboxId = `${baseId}-listbox`;
  const getOptionId = (index: number) => `${baseId}-option-${index}`;

  const searchString = !!setSearchStringControlled ? searchStringControlled : searchStringInternal;
  const setSearchString = setSearchStringControlled || setSearchStringInternal;

  useEffect(() => {
    hasFocus ? open() : close();
  }, [hasFocus]);

  useEffect(() => {
    if (defaultSelectOnload && options.length > 0) {
      let index;
      const firstOption = options.find((opt, i) => {
        index = i;
        return !opt.isHeader || !opt.isSubHeader;
      });
      setValue(isMultiple ? `${name}[${index}]` : name, (firstOption as MultiSelectOption).value);
    }

    const opts = options.slice();
    if (sort) opts.sort((a, b) => a.label.localeCompare(b.label));
    setLocalOptions(opts);
  }, [options]);

  useEffect(() => {
    if (!isMultiple) register(name, { required, valueAsNumber });
  }, [register, isMultiple]);

  useOnClickOutside(hasFocus, setHasFocus, containerRef);
  useOnEscapePress(blur, [blur]);

  const handleInput = (e: ChangeEvent<HTMLInputElement>) => setSearchString(e.currentTarget.value);

  function blur(e?: KeyboardEvent) {
    if (e instanceof KeyboardEvent && e.currentTarget instanceof HTMLElement) e.currentTarget?.blur();

    const container = containerRef.current;
    if (container?.contains(document.activeElement) && document.activeElement?.tagName !== 'INPUT') {
      skipOpenOnFocus.current = true;
      container.querySelector('input')?.focus();
      skipOpenOnFocus.current = false;
    }

    setHasFocus(false);
    setSearchString('');
  }

  const limitReached = isMultiple && limit && watched && watched.filter(Boolean).length >= limit;

  const visibleOptions = useMemo(() => {
    const toRender: MultiSelectOption[] = [];
    localOptions
      .filter((o) => o.isHeader || o.label.toLowerCase().includes((searchString || '').toLowerCase()))
      .filter((o) => (limitReached ? o.isHeader || watched?.some((s: string | number) => s == o.value) : true))
      .forEach((o, i, src) => {
        if (o.isHeader) {
          if (toRender[toRender.length - 1]?.isHeader) toRender.pop();
          if (i === src.length - 1) return;
        }

        toRender.push(o);
      });

    return toRender;
  }, [localOptions, searchString, limitReached, watched]);

  const selectableIndexes = useMemo(
    () =>
      visibleOptions.reduce<number[]>((acc, o, i) => {
        if (!o.isHeader && !o.isSubHeader) acc.push(i);

        return acc;
      }, []),
    [visibleOptions],
  );

  useEffect(() => {
    if (!isOpen) setActiveIndex(-1);
  }, [isOpen]);

  useEffect(() => {
    setActiveIndex(-1);
  }, [searchString]);

  useEffect(() => {
    if (!isOpen || activeIndex < 0) return;

    const activeOption = document.getElementById(getOptionId(activeIndex));
    activeOption?.focus?.();
    activeOption?.scrollIntoView?.({ block: 'nearest' });
  }, [activeIndex, isOpen]);

  const selectSingleOption = (value: string | number) => {
    if (handleSideEffect && value && watched !== value) {
      valueAsNumber ? (handleSideEffect as HandleNumber)(value as number) : (handleSideEffect as HandleString)(value as string);
    }

    if (watched === value) {
      reset({ ...getValues(), [name]: undefined });
    } else {
      setValue(name, value, {
        shouldDirty: true,
        shouldValidate: true,
      });
    }

    blur();
  };

  const moveActiveOption = (direction: 1 | -1) => {
    if (!selectableIndexes.length) return;

    const position = selectableIndexes.indexOf(activeIndex);
    if (position === -1) {
      setActiveIndex(direction === 1 ? selectableIndexes[0] : selectableIndexes[selectableIndexes.length - 1]);
      return;
    }

    const next = (position + direction + selectableIndexes.length) % selectableIndexes.length;
    setActiveIndex(selectableIndexes[next]);
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Multi-select rows render native checkboxes that are tabbable on their own.
    if (isMultiple) return;

    switch (e.key) {
      case 'ArrowDown':
      case 'ArrowUp':
        e.preventDefault();
        if (!isOpen) setHasFocus(true);

        moveActiveOption(e.key === 'ArrowDown' ? 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (!isOpen) {
          setHasFocus(true);
          break;
        }

        if (visibleOptions[activeIndex]) selectSingleOption(visibleOptions[activeIndex].value);

        break;
    }
  };

  const handleOptionKeyDown = (e: React.KeyboardEvent<HTMLDivElement>, value: string | number) => {
    switch (e.key) {
      case 'ArrowDown':
      case 'ArrowUp':
        e.preventDefault();
        moveActiveOption(e.key === 'ArrowDown' ? 1 : -1);
        break;
      case 'Home':
      case 'End':
        if (!selectableIndexes.length) break;

        e.preventDefault();
        setActiveIndex(e.key === 'Home' ? selectableIndexes[0] : selectableIndexes[selectableIndexes.length - 1]);
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        selectSingleOption(value);
        break;
    }
  };

  const handleContainerBlur = (e: React.FocusEvent<HTMLDivElement>) => {
    if (containerRef.current?.contains(e.relatedTarget as Node)) return;

    setHasFocus(false);
    setSearchString('');
  };

  let inputValue = '';
  if (hasFocus) {
    inputValue = searchString || '';
  } else if (isMultiple && watched && !showValues) {
    if (limit) {
      inputValue = t('Selected {{count}}/{{max}} (of {{total}})', {
        count: watched.filter(Boolean).length,
        max: limit,
        total: options.filter((o) => !o.unselectable).length,
      });
    } else {
      inputValue = t('Selected {{count}}/{{total}}', {
        count: watched.filter(Boolean).length,
        total: options.filter((o) => !o.unselectable).length,
      });
    }
  } else if (isMultiple && watched && showValues) {
    inputValue = watched.filter(Boolean).join(', ');
  } else {
    inputValue =
      localOptions.find((o) => (isMultiple ? watched?.some((s: string | number) => o.value === s) : watched === o.value))
        ?.label || '';
  }

  return (
    <StyledDropdownWrapper ref={containerRef} $fullwidth={fullwidth} onBlur={handleContainerBlur}>
      <InputBasic
        disabled={disabled}
        autoComplete={AutoComplete.on}
        label={label}
        hiddenLabel={hiddenLabel}
        icon={icon}
        rotatedIcon={isOpen}
        loading={loading}
        required={required}
        placeholder={placeholder}
        aria-required={required ? 'true' : 'false'}
        role="combobox"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-controls={listboxId}
        aria-autocomplete="list"
        value={inputValue}
        onFocus={() => !disabled && !skipOpenOnFocus.current && setHasFocus(true)}
        onChange={handleInput}
        onKeyDown={handleInputKeyDown}
        overline={true}
        newTheme={newTheme}
        onIconClick={() => !disabled && setHasFocus(!isOpen)}
        type="text"
        cursor="pointer"
      />
      <StyledDropdownMultiSelect
        open={isOpen}
        id={listboxId}
        role="listbox"
        data-testid="listbox"
        aria-label="MultiSelectDropdown"
        // Keep focus on the input when options are clicked, so the focusout
        // handler above does not close the dropdown before the click lands.
        onMouseDown={(e: React.MouseEvent) => e.preventDefault()}
      >
        <MultiSelectRows
          {...{ searchString, isMultiple, name, valueAsNumber, handleSideEffect, getOptionId }}
          options={visibleOptions}
          onSingleSelect={selectSingleOption}
          onOptionKeyDown={handleOptionKeyDown}
          onOptionFocus={setActiveIndex}
        />
      </StyledDropdownMultiSelect>
      {error && <StyledError>{error.message}</StyledError>}
    </StyledDropdownWrapper>
  );
}
